import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import CatalogCard from '@shared/components/CatalogCard/CatalogCard';
import { useAuth } from '../context/AuthContext';
import { curriculumAPI } from '../services/api';
import LearnerNav from '../components/nav/LearnerNav';

const API_BASE = '/scenario-os/curriculum';

function authHeaders() {
  const token = localStorage.getItem('scenario_os_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchResources({ domain, resource_type, difficulty } = {}) {
  const params = new URLSearchParams();
  if (domain) params.set('domain', domain);
  if (resource_type) params.set('resource_type', resource_type);
  if (difficulty) params.set('difficulty', difficulty);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await fetch(`${API_BASE}/resources${qs}`, {
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return data.resources || [];
}

async function deleteResource(resourceUrl) {
  const b64 = btoa(resourceUrl).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  const res = await fetch(`${API_BASE}/resources/${b64}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

const DIFFICULTY_ICON = { beginner: '🟢', intermediate: '🟡', advanced: '🔴' };
const DIFFICULTY_COLOR = { beginner: '#166534', intermediate: '#92400e', advanced: '#991b1b' };
const DIFFICULTY_BG    = { beginner: '#dcfce7', intermediate: '#fef3c7', advanced: '#fee2e2' };


const DIFFICULTY_OPTIONS = [
  { value: '', label: 'All Levels' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
];

export default function ResourceLibraryPage() {
  const navigate = useNavigate();
  const { learner } = useAuth();
  const [activeTab, setActiveTab] = useState('curated_resource');
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [domainFilter, setDomainFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingUrl, setDeletingUrl] = useState(null);
  const [viewingCanvas, setViewingCanvas] = useState(null);
  const [domainOptions, setDomainOptions] = useState([{ value: '', label: 'All Domains' }]);

  useEffect(() => {
    curriculumAPI.listDomains()
      .then(({ data }) => {
        const opts = [{ value: '', label: 'All Domains' }];
        for (const d of (data.domains || [])) {
          opts.push({ value: d.domain_id, label: d.display_name || d.domain_id });
        }
        setDomainOptions(opts);
      })
      .catch(() => {
        const enrolled = learner?.enrolled_domains || learner?.domains || [];
        setDomainOptions([
          { value: '', label: 'All Domains' },
          ...enrolled.map((id) => ({ value: id, label: id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) })),
        ]);
      });
  }, [learner]);

  useEffect(() => {
    setLoading(true);
    setError('');
    fetchResources()
      .then(setResources)
      .catch((e) => setError(e.message || 'Failed to load resources.'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return resources.filter((r) => {
      if (r.resource_type !== activeTab) return false;
      if (domainFilter && r.domain !== domainFilter) return false;
      if (difficultyFilter && r.difficulty !== difficultyFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!(r.title || '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [resources, activeTab, domainFilter, difficultyFilter, searchQuery]);

  const handleDelete = async (resource) => {
    setDeletingUrl(resource.resource_url);
    try {
      await deleteResource(resource.resource_url);
      setResources((prev) => prev.filter((r) => r.resource_url !== resource.resource_url));
    } catch {
      // silently ignore — resource stays in list
    } finally {
      setDeletingUrl(null);
    }
  };

  const buildBadges = (r) => {
    const badges = [];
    if (r.domain) badges.push({ label: r.domain.replace(/_/g, ' '), color: '#4f46e5' });
    if (r.difficulty) {
      badges.push({
        label: `${DIFFICULTY_ICON[r.difficulty] || ''} ${r.difficulty}`,
        color: DIFFICULTY_COLOR[r.difficulty] || '#374151',
      });
    }
    if (r.coach_pushed) badges.push({ label: '📤 Coach recommended', color: '#0891b2' });
    return badges;
  };

  const buildTags = (r) => {
    const tags = [];
    if (r.module_id) tags.push(`From: ${r.module_id}`);
    return tags;
  };

  const buildActions = (r) => {
    const actions = [];
    if (activeTab === 'curated_resource' && r.resource_url) {
      actions.push({
        label: 'Open →',
        variant: 'primary',
        onClick: () => window.open(r.resource_url, '_blank', 'noopener,noreferrer'),
      });
    } else if (activeTab === 'prompt_canvas') {
      actions.push({
        label: 'View Canvas',
        variant: 'primary',
        onClick: () => setViewingCanvas(r),
      });
    }
    actions.push({
      label: deletingUrl === r.resource_url ? 'Removing…' : 'Remove',
      variant: 'danger',
      disabled: deletingUrl === r.resource_url,
      onClick: () => handleDelete(r),
    });
    return actions;
  };

  return (
    <div style={{ minHeight: '100vh', background: '#FFF8F0' }}>
      <LearnerNav />
      <div style={pageStyle}>
      <header style={headerStyle}>
        <h1 style={{ margin: 0, fontSize: 24, color: '#1E1E24' }}>My Resource Library</h1>
      </header>

      {/* Tabs */}
      <div style={tabBarStyle}>
        {[
          { key: 'curated_resource', label: '📚 Resources' },
          { key: 'prompt_canvas', label: '🎨 My Canvas' },
        ].map((tab) => (
          <button
            key={tab.key}
            style={tabBtnStyle(activeTab === tab.key)}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div style={filterBarStyle}>
        <select
          style={selectStyle}
          value={domainFilter}
          onChange={(e) => setDomainFilter(e.target.value)}
        >
          {domainOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <select
          style={selectStyle}
          value={difficultyFilter}
          onChange={(e) => setDifficultyFilter(e.target.value)}
        >
          {DIFFICULTY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <input
          style={searchInputStyle}
          type="text"
          placeholder="Search by title…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        {(domainFilter || difficultyFilter || searchQuery) && (
          <button
            style={clearBtnStyle}
            onClick={() => { setDomainFilter(''); setDifficultyFilter(''); setSearchQuery(''); }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Content */}
      {loading && <div style={mutedStyle}>Loading your library…</div>}
      {error && <div style={errorStyle}>{error}</div>}

      {!loading && !error && filtered.length === 0 && (
        <div style={emptyStateStyle}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>
            {activeTab === 'curated_resource' ? '📚' : '🎨'}
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#1f2937' }}>
            {resources.filter((r) => r.resource_type === activeTab).length === 0
              ? 'No resources saved yet. Complete modules to build your library.'
              : 'No resources match your filters.'}
          </div>
          <div style={{ fontSize: 14, color: '#6b7280' }}>
            Resources are automatically saved as you complete lesson segments.
          </div>
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div style={gridStyle}>
          {filtered.map((r) => (
            <CatalogCard
              key={r.resource_url}
              id={r.resource_url}
              title={r.title || 'Untitled Resource'}
              description={r.source_name || undefined}
              badges={buildBadges(r)}
              tags={buildTags(r)}
              footer={r.saved_at ? {
                left: `Saved ${new Date(r.saved_at).toLocaleDateString()}`,
                right: r.last_accessed ? `Accessed ${new Date(r.last_accessed).toLocaleDateString()}` : undefined,
              } : undefined}
              actions={buildActions(r)}
            />
          ))}
        </div>
      )}

      {viewingCanvas && (
        <CanvasViewerModal canvas={viewingCanvas} onClose={() => setViewingCanvas(null)} />
      )}
      </div>
    </div>
  );
}

// ─── Canvas Viewer Modal ─────────────────────────────────────────────────────

function CanvasViewerModal({ canvas, onClose }) {
  return (
    <>
      <div onClick={onClose} style={modalOverlayStyle} />
      <div style={modalPanelStyle}>
        <div style={modalHeaderStyle}>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1e293b' }}>
              {canvas.title || 'Untitled Canvas'}
            </h3>
            {canvas.difficulty && (
              <span style={{
                fontSize: 12, color: DIFFICULTY_COLOR[canvas.difficulty] || '#374151',
                background: DIFFICULTY_BG[canvas.difficulty] || '#f3f4f6',
                padding: '2px 8px', borderRadius: 10, marginTop: 4, display: 'inline-block',
              }}>
                {DIFFICULTY_ICON[canvas.difficulty] || ''} {canvas.difficulty}
              </span>
            )}
          </div>
          <button onClick={onClose} style={modalCloseBtnStyle}>✕</button>
        </div>

        <div style={modalBodyStyle}>
          {/* Canvas metadata */}
          {(canvas.source_name || canvas.module_id || canvas.saved_at) && (
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 12, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {canvas.source_name && <span>📚 {canvas.source_name}</span>}
              {canvas.module_id && <span>📦 Module: {canvas.module_id}</span>}
              {canvas.saved_at && <span>🕐 {new Date(canvas.saved_at).toLocaleDateString()}</span>}
            </div>
          )}

          {/* Format 1: steps array (old format) */}
          {canvas.steps && canvas.steps.length > 0 && (
            <>
              {canvas.steps.map((step, i) => (
                <div key={i} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                    {i + 1}. {step.label || `Step ${i + 1}`}
                  </div>
                  <pre style={modalCodeStyle}>
                    {step.content || ''}
                  </pre>
                </div>
              ))}
              {canvas.key_insight && (
                <div style={modalInsightStyle}>
                  <strong>Key Insight:</strong> {canvas.key_insight}
                </div>
              )}
            </>
          )}

          {/* Format 2: canvas.content field */}
          {(!canvas.steps || canvas.steps.length === 0) && canvas.content && (
            <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
              {canvas.content}
            </div>
          )}

          {/* Format 3: prompt_text or description */}
          {(!canvas.steps || canvas.steps.length === 0) && !canvas.content && (canvas.prompt_text || canvas.description) && (
            <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
              {canvas.prompt_text || canvas.description}
            </div>
          )}

          {/* Format 4: resource_url link — only if URL is a valid non-empty string */}
          {canvas.resource_url && typeof canvas.resource_url === 'string' && canvas.resource_url.startsWith('http') && (
            <div style={{ marginTop: 12 }}>
              <a
                href={canvas.resource_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#4f46e5', fontSize: 14, fontWeight: 600 }}
              >
                🔗 View Original Resource
              </a>
            </div>
          )}

          {/* Fallback: no content stored yet */}
          {(!canvas.steps || canvas.steps.length === 0) && !canvas.content && !canvas.prompt_text && !canvas.description && !canvas.key_insight && (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#9ca3af', fontSize: 14 }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>🎨</div>
              <div style={{ fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                {canvas.title || 'Canvas Template'}
              </div>
              <div style={{ maxWidth: 360, margin: '0 auto', lineHeight: 1.6 }}>
                This canvas template is saved to your library.
                Continue your training sessions to generate personalized content.
              </div>
              {canvas.topic_summary && (
                <div style={{ marginTop: 10, color: '#374151', fontStyle: 'italic' }}>
                  Topic: {canvas.topic_summary}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

const modalOverlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
};

const modalPanelStyle = {
  position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
  width: '90%', maxWidth: 600, maxHeight: '80vh',
  background: '#fff', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  display: 'flex', flexDirection: 'column', zIndex: 1001,
};

const modalHeaderStyle = {
  display: 'flex', alignItems: 'flex-start', gap: 12,
  padding: '16px 20px', borderBottom: '1px solid #e5e7eb',
};

const modalCloseBtnStyle = {
  background: 'transparent', border: 'none', fontSize: 18,
  color: '#94a3b8', cursor: 'pointer', padding: '4px 8px', lineHeight: 1,
};

const modalBodyStyle = {
  padding: 20, overflowY: 'auto', flex: 1,
};

const modalCodeStyle = {
  background: '#f1f5f9', borderRadius: 6, padding: '8px 10px',
  fontSize: 12, lineHeight: 1.5, margin: 0, whiteSpace: 'pre-wrap',
  wordBreak: 'break-word', color: '#0f172a',
};

const modalInsightStyle = {
  background: '#fef3c7', borderRadius: 6, padding: 10,
  fontSize: 13, color: '#92400e', marginTop: 8,
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const pageStyle = {
  maxWidth: 1100,
  margin: '24px auto',
  padding: '0 24px',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  marginBottom: 24,
  paddingBottom: 16,
  borderBottom: '1px solid #e5e7eb',
};

const backBtnStyle = {
  background: 'transparent',
  border: 'none',
  color: '#3b82f6',
  cursor: 'pointer',
  fontSize: 14,
  padding: 4,
};

const tabBarStyle = {
  display: 'flex',
  gap: 4,
  marginBottom: 20,
  borderBottom: '2px solid #e5e7eb',
};

const tabBtnStyle = (active) => ({
  padding: '10px 20px',
  background: 'transparent',
  border: 'none',
  borderBottom: active ? '2px solid #3b82f6' : '2px solid transparent',
  marginBottom: -2,
  cursor: 'pointer',
  fontSize: 15,
  fontWeight: active ? 700 : 400,
  color: active ? '#1d4ed8' : '#6b7280',
  transition: 'all 0.15s',
});

const filterBarStyle = {
  display: 'flex',
  gap: 10,
  marginBottom: 20,
  flexWrap: 'wrap',
  alignItems: 'center',
};

const selectStyle = {
  padding: '8px 12px',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  fontSize: 14,
  background: '#fff',
  color: '#374151',
  cursor: 'pointer',
};

const searchInputStyle = {
  padding: '8px 12px',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  fontSize: 14,
  flex: '1 1 200px',
  minWidth: 160,
  maxWidth: 320,
};

const clearBtnStyle = {
  padding: '8px 12px',
  background: 'transparent',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  fontSize: 13,
  color: '#6b7280',
  cursor: 'pointer',
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  gap: 16,
};

const mutedStyle = { color: '#6b7280', fontSize: 14, padding: '24px 0' };

const errorStyle = {
  color: '#b3261e',
  background: 'rgba(239,68,68,0.08)',
  border: '1px solid rgba(239,68,68,0.3)',
  padding: 12,
  borderRadius: 6,
  fontSize: 14,
  marginBottom: 16,
};

const emptyStateStyle = {
  textAlign: 'center',
  padding: '48px 24px',
  background: '#f9fafb',
  borderRadius: 12,
  border: '1px dashed #d1d5db',
};
